import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  ENUM_RECRUITMENT_JOB_STATUS,
  ENUM_RECRUITMENT_NOTE_SIGNAL,
} from "@/utils/enums/recruitment.enum";
import type { IApiResponse } from "@/types";
import type {
  IAddToTalentPoolPayload,
  IAiGenerateEvaluationCriteriaPayload,
  IAiGenerateEvaluationCriteriaResult,
  IAiGenerateInterviewQuestionsPayload,
  IAiGenerateInterviewQuestionsResult,
  IAiJobScoreResult,
  IAiScoreJobPayload,
  IAiGenerateJobDescriptionPayload,
  IAiGenerateJobDescriptionResult,
  IAiGenerateJobPostPayload,
  IAiGenerateScreeningQuestionsPayload,
  IAiJobDraft,
  IAiSuggestJobRequirementsPayload,
  IAiGenerateScreeningQuestionsResult,
  IAiRecommendInterviewTeamResult,
  IAiSuggestInterviewPlanPayload,
  IAiSuggestInterviewPlanResult,
  IAiSuggestJobRequirementsResult,
  IAnalysisResult,
  ICandidateComparison,
  IApplication,
  IApplicationAiMatch,
  IApplicationDetail,
  IApplicationNote,
  IRecordScreeningDecisionPayload,
  IBulkImportParsedItem,
  ICandidateListRow,
  IJob,
  IExternalParticipantSuggestion,
  IJobActivityEntry,
  IJobInsights,
  JobInsightsPeriod,
  IJobCategory,
  IJobFilterOptions,
  IJobListItem,
  IJobNote,
  IJobSegmentCounts,
  IPastApplicantRow,
  IReferral,
  IRejectApplicationPayload,
  ICreateJobTemplatePayload,
  IJobTemplate,
  ITalentPoolEntry,
  IUpdateRecruitmentSettingsPayload,
  IUpsertJobPayload,
  IRecruitmentCity,
  IRecruitmentCountry,
  IDashboardFunnel,
  IDashboardPipeline,
  IRecruitmentDashboard,
  IRecruitmentDashboardOverview,
  DashboardPipelinePeriod,
  DashboardPipelineTab,
  IRecruitmentIntegrationStatus,
  CareersImageKind,
  ICareersTheme,
  IUpdateCareersThemePayload,
  IRecruitmentSettings,
} from "@/types/(company)/recruitment.interface";

export interface IListCandidatesParams {
  page?: number;
  limit?: number;
  jobId?: number;
  stage?: string;
  source?: string;
  /** match = highest AI match first (unscored last); name = candidate A–Z. */
  sortBy?: "match" | "name";
  search?: string;
  /** Include applications with a closed outcome (hired, rejected, withdrawn). Default false. */
  includeClosed?: boolean;
}

export interface IListTalentPoolParams {
  segment: "pool" | "past_applicants" | "referrals";
  page?: number;
  limit?: number;
  search?: string;
  /** Pool segment only — narrows to one origin. */
  source?: string;
}

export interface IListJobsParams {
  page?: number;
  limit?: number;
  segment?: "open" | "draft" | "closed" | "archived" | "deleted";
  department?: string;
  location?: string;
  status?: ENUM_RECRUITMENT_JOB_STATUS;
  sortBy?: "recent" | "age";
  search?: string;
}

/**
 * Tag id for a job's pipeline list, kept out of the application-id namespace.
 *
 * Both the pipeline list (keyed by job) and a candidate's detail (keyed by
 * application) live under the `RecruitmentApplication` tag, so a bare `jobId`
 * and a bare application id could collide — job 12's pipeline and application
 * 12 were the same cache tag. Prefixing the list keeps the two apart, which is
 * what lets a mutation invalidate exactly what it changed.
 */
const pipelineTag = (jobId: number) => `PIPELINE_${jobId}`;

/** Provided by every job's board and analysis, so one tag refreshes them all. */
const ALL_PIPELINES_TAG = "ALL_PIPELINES";

/**
 * Everything that shows a stored AI match score. A re-score changes it, and it
 * is read well beyond the one application: the job list's top match, the
 * dashboard, the candidates list, every pipeline board and the comparison.
 */
export const AI_MATCH_DEPENDENT_TAGS: ReadonlyArray<{
  type: "RecruitmentJob" | "RecruitmentApplication" | "RecruitmentCandidate";
  id: string;
}> = [
  { type: "RecruitmentJob", id: "LIST" },
  { type: "RecruitmentJob", id: "DASHBOARD" },
  { type: "RecruitmentCandidate", id: "LIST" },
  { type: "RecruitmentApplication", id: ALL_PIPELINES_TAG },
  { type: "RecruitmentApplication", id: "COMPARE" },
];

/** The upload / remove route for one careers-page image. */
const careersImageUrl = (kind: CareersImageKind) =>
  kind === "logo"
    ? APIEndpoints.Recruitment.CareersThemeLogo
    : kind === "logoIcon"
      ? APIEndpoints.Recruitment.CareersThemeLogoIcon
      : APIEndpoints.Recruitment.CareersThemeBanner;

export const companyRecruitmentApi = createApi({
  reducerPath: "companyRecruitmentApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "RecruitmentJob",
    "RecruitmentJobNote",
    "RecruitmentApplication",
    "RecruitmentApplicationNote",
    "RecruitmentCandidate",
    "RecruitmentTalentPool",
    "RecruitmentReferral",
    "RecruitmentSettings",
    "RecruitmentJobTemplate",
  ],
  endpoints: (builder) => ({
    getJobs: builder.query<
      IApiResponse<IJobListItem[]>,
      IListJobsParams | void
    >({
      query: (params) => ({
        url: APIEndpoints.Recruitment.Jobs,
        params: params ?? {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((job) => ({
                type: "RecruitmentJob" as const,
                id: job.id,
              })),
              { type: "RecruitmentJob" as const, id: "LIST" },
            ]
          : [{ type: "RecruitmentJob" as const, id: "LIST" }],
    }),

    getJobSegmentCounts: builder.query<IApiResponse<IJobSegmentCounts>, void>({
      query: () => ({ url: APIEndpoints.Recruitment.JobSegmentCounts }),
      providesTags: [{ type: "RecruitmentJob", id: "SEGMENT_COUNTS" }],
    }),

    getJobFilterOptions: builder.query<IApiResponse<IJobFilterOptions>, void>({
      query: () => ({ url: APIEndpoints.Recruitment.JobFilterOptions }),
      providesTags: [{ type: "RecruitmentJob", id: "FILTER_OPTIONS" }],
    }),

    /**
     * The category taxonomy the job form's picker offers. Global reference
     * data, not this company's own records — same shape every tenant sees.
     */
    getRecruitmentJobCategories: builder.query<
      IApiResponse<IJobCategory[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.JobCategories }),
      providesTags: [{ type: "RecruitmentJob", id: "CATEGORIES" }],
    }),

    /*
      Cached under one key for the whole company — it is a directory, not a
      per-job read, and every round on every job asks the same question.
      Invalidated by a job save, because saving is how somebody new gets into
      it.
    */
    getJobActivity: builder.query<IApiResponse<IJobActivityEntry[]>, number>({
      query: (id) => ({ url: APIEndpoints.Recruitment.JobActivity(id) }),
      providesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id: `ACTIVITY_${id}` },
      ],
    }),

    /** A role's Overview numbers for a reporting window. */
    getJobInsights: builder.query<
      IApiResponse<IJobInsights>,
      { id: number; period: JobInsightsPeriod }
    >({
      query: ({ id, period }) => ({
        url: APIEndpoints.Recruitment.JobInsights(id),
        params: period === "30d" ? undefined : { period },
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "RecruitmentJob", id: `INSIGHTS_${id}` },
      ],
    }),

    getJobExternalParticipants: builder.query<
      IApiResponse<IExternalParticipantSuggestion[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.JobExternalParticipants }),
      providesTags: [{ type: "RecruitmentJob", id: "EXTERNAL_PARTICIPANTS" }],
    }),

    getJobById: builder.query<IApiResponse<IJob>, number>({
      query: (id) => ({ url: APIEndpoints.Recruitment.JobById(id) }),
      providesTags: (_result, _error, id) => [{ type: "RecruitmentJob", id }],
    }),

    createJob: builder.mutation<IApiResponse<IJob>, IUpsertJobPayload>({
      query: (body) => ({
        url: APIEndpoints.Recruitment.Jobs,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    updateJob: builder.mutation<
      IApiResponse<IJob>,
      { id: number; body: IUpsertJobPayload }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.Recruitment.JobById(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        /*
          Saving a job is how a new external interviewer gets into the
          directory — they are typed onto a round and exist nowhere until it
          is written. Without this the person you just added is missing from
          the suggestions until a reload, which reads as the feature not
          working on exactly the first use.
        */
        { type: "RecruitmentJob", id: "EXTERNAL_PARTICIPANTS" },
      ],
    }),

    publishJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobPublish(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    closeJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobClose(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    /** Hides a role from the active lists but keeps it, and its pipeline, on record. */
    archiveJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobArchive(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    /** Brings a closed or archived role back to open. */
    reopenJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobReopen(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    /** Copies a role's configuration into a new draft. */
    duplicateJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobDuplicate(id),
        method: "POST",
      }),
      invalidatesTags: [
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    deleteJob: builder.mutation<void, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobById(id),
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    /** Undoes a soft delete — the only way a job leaves the Deleted segment. */
    restoreJob: builder.mutation<IApiResponse<IJob>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobRestore(id),
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "RecruitmentJob", id },
        { type: "RecruitmentJob", id: "LIST" },
        { type: "RecruitmentJob", id: "SEGMENT_COUNTS" },
      ],
    }),

    getJobNotes: builder.query<IApiResponse<IJobNote[]>, number>({
      query: (jobId) => ({ url: APIEndpoints.Recruitment.JobNotes(jobId) }),
      providesTags: (_result, _error, jobId) => [
        { type: "RecruitmentJobNote", id: jobId },
      ],
    }),

    addJobNote: builder.mutation<
      IApiResponse<IJobNote>,
      { jobId: number; body: string }
    >({
      query: ({ jobId, body }) => ({
        url: APIEndpoints.Recruitment.JobNotes(jobId),
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentJobNote", id: jobId },
      ],
    }),

    deleteJobNote: builder.mutation<void, { jobId: number; noteId: number }>({
      query: ({ jobId, noteId }) => ({
        url: APIEndpoints.Recruitment.JobNoteById(jobId, noteId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentJobNote", id: jobId },
      ],
    }),

    getPipelineApplications: builder.query<
      IApiResponse<IApplication[]>,
      number
    >({
      query: (jobId) => ({
        url: APIEndpoints.Recruitment.Applications,
        params: { jobId },
      }),
      providesTags: (_result, _error, jobId) => [
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
        { type: "RecruitmentApplication", id: ALL_PIPELINES_TAG },
      ],
    }),

    moveApplicationStage: builder.mutation<
      IApiResponse<IApplication>,
      { id: number; jobId: number; stage: string }
    >({
      query: ({ id, stage }) => ({
        url: APIEndpoints.Recruitment.ApplicationStage(id),
        method: "PATCH",
        body: { stage },
      }),
      // The candidate's own screen has to move too, not just the board it was
      // dragged on: advancing from the detail page left the header reading
      // "Screened" with a "Move to Shortlisted" button over an application the
      // server had already shortlisted.
      invalidatesTags: (_result, _error, { id, jobId }) => [
        { type: "RecruitmentApplication", id },
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
        { type: "RecruitmentCandidate", id: "LIST" },
      ],
    }),

    getApplicationDetail: builder.query<
      IApiResponse<IApplicationDetail>,
      number
    >({
      query: (id) => ({ url: APIEndpoints.Recruitment.ApplicationById(id) }),
      providesTags: (_result, _error, id) => [
        { type: "RecruitmentApplication", id },
      ],
    }),

    // The recruiter's own screening verdict — invalidates the application so the
    // screening tab and the timeline both pick up the new decision.
    recordScreeningDecision: builder.mutation<
      IApiResponse<IApplication>,
      { id: number } & IRecordScreeningDecisionPayload
    >({
      query: ({ id, ...body }) => ({
        url: APIEndpoints.Recruitment.ApplicationScreeningDecision(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "RecruitmentApplication", id },
      ],
    }),

    // Recheck: Jobniq asks the AI again and replaces the stored match. The
    // answer comes back marked as scoring; the detail is refetched (and polled
    // by the screening tab) until the new answer lands. Every other view of the
    // score is refetched now and again when it lands (`useRefreshOnAiMatchScored`).
    recheckAiMatch: builder.mutation<IApiResponse<IApplicationAiMatch>, number>(
      {
        query: (id) => ({
          url: APIEndpoints.Recruitment.ApplicationAiMatch(id),
          method: "POST",
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: "RecruitmentApplication", id },
          ...AI_MATCH_DEPENDENT_TAGS,
        ],
      },
    ),

    getApplicationNotes: builder.query<
      IApiResponse<IApplicationNote[]>,
      number
    >({
      query: (id) => ({ url: APIEndpoints.Recruitment.ApplicationNotes(id) }),
      providesTags: (_result, _error, id) => [
        { type: "RecruitmentApplicationNote", id },
      ],
    }),

    addApplicationNote: builder.mutation<
      IApiResponse<IApplicationNote>,
      { id: number; body: string; signal?: ENUM_RECRUITMENT_NOTE_SIGNAL }
    >({
      query: ({ id, body, signal }) => ({
        url: APIEndpoints.Recruitment.ApplicationNotes(id),
        method: "POST",
        body: signal ? { body, signal } : { body },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "RecruitmentApplicationNote", id },
      ],
    }),

    deleteApplicationNote: builder.mutation<
      void,
      { id: number; noteId: number }
    >({
      query: ({ id, noteId }) => ({
        url: APIEndpoints.Recruitment.ApplicationNoteById(id, noteId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "RecruitmentApplicationNote", id },
      ],
    }),

    getCandidates: builder.query<
      IApiResponse<ICandidateListRow[]>,
      IListCandidatesParams | void
    >({
      query: (params) => ({
        url: APIEndpoints.Recruitment.Candidates,
        params: params ?? {},
      }),
      providesTags: [{ type: "RecruitmentCandidate", id: "LIST" }],
    }),

    /** "Start with AI": a whole job draft. Can take up to ~90 s; nothing is saved. */
    aiGenerateJobPost: builder.mutation<
      IApiResponse<IAiJobDraft>,
      IAiGenerateJobPostPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiJobPost,
        method: "POST",
        body,
      }),
    }),

    aiGenerateJobDescription: builder.mutation<
      IApiResponse<IAiGenerateJobDescriptionResult>,
      IAiGenerateJobDescriptionPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiJobDescription,
        method: "POST",
        body,
      }),
    }),

    aiSuggestJobRequirements: builder.mutation<
      IApiResponse<IAiSuggestJobRequirementsResult>,
      IAiSuggestJobRequirementsPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiJobRequirements,
        method: "POST",
        body,
      }),
    }),

    aiGenerateScreeningQuestions: builder.mutation<
      IApiResponse<IAiGenerateScreeningQuestionsResult>,
      IAiGenerateScreeningQuestionsPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiScreeningQuestions,
        method: "POST",
        body,
      }),
    }),

    /** "Suggest interview plan": a whole process. Can take up to ~90 s; nothing is saved. */
    aiSuggestInterviewPlan: builder.mutation<
      IApiResponse<IAiSuggestInterviewPlanResult>,
      IAiSuggestInterviewPlanPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiInterviewPlan,
        method: "POST",
        body,
      }),
    }),

    aiRecommendInterviewTeam: builder.mutation<
      IApiResponse<IAiRecommendInterviewTeamResult>,
      { roundName: string; roundType: string; department?: string }
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiInterviewTeam,
        method: "POST",
        body,
      }),
    }),

    aiGenerateEvaluationCriteria: builder.mutation<
      IApiResponse<IAiGenerateEvaluationCriteriaResult>,
      IAiGenerateEvaluationCriteriaPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiEvaluationCriteria,
        method: "POST",
        body,
      }),
    }),

    aiGenerateInterviewQuestions: builder.mutation<
      IApiResponse<IAiGenerateInterviewQuestionsResult>,
      IAiGenerateInterviewQuestionsPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiInterviewQuestions,
        method: "POST",
        body,
      }),
    }),

    /** "Get AI Score": scores the form as it stands — saved or not. */
    aiScoreJob: builder.mutation<
      IApiResponse<IAiJobScoreResult>,
      IAiScoreJobPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.AiJobScore,
        method: "POST",
        body,
      }),
    }),

    rejectApplication: builder.mutation<
      IApiResponse<IApplication>,
      { id: number; jobId: number; body: IRejectApplicationPayload }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.Recruitment.ApplicationReject(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id, jobId }) => [
        { type: "RecruitmentApplication", id },
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
        { type: "RecruitmentCandidate", id: "LIST" },
      ],
    }),

    /*
      Tagged with the job's pipeline: shortlisting someone (here or by dragging
      a card) changes who is left to analyse, and the ranking refetches.
    */
    analyzeCandidates: builder.query<IApiResponse<IAnalysisResult>, number>({
      query: (jobId) => ({
        url: APIEndpoints.Recruitment.ApplicationsAnalysis,
        params: { jobId },
      }),
      providesTags: (_result, _error, jobId) => [
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
        { type: "RecruitmentApplication", id: ALL_PIPELINES_TAG },
      ],
    }),

    /** 2–4 applications side by side from their stored AI match. */
    compareCandidates: builder.query<
      IApiResponse<ICandidateComparison>,
      number[]
    >({
      query: (ids) => ({
        url: APIEndpoints.Recruitment.ApplicationsCompare,
        params: { ids: ids.join(",") },
      }),
      providesTags: [{ type: "RecruitmentApplication", id: "COMPARE" }],
    }),

    approveFromAnalysis: builder.mutation<
      IApiResponse<{ moved: number }>,
      { applicationIds: number[]; jobId: number }
    >({
      query: ({ applicationIds }) => ({
        url: APIEndpoints.Recruitment.ApplicationsAnalysisApprove,
        method: "POST",
        body: { applicationIds },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
      ],
    }),

    getJobTemplates: builder.query<IApiResponse<IJobTemplate[]>, void>({
      query: () => ({ url: APIEndpoints.Recruitment.JobTemplates }),
      providesTags: [{ type: "RecruitmentJobTemplate", id: "LIST" }],
    }),

    createJobTemplate: builder.mutation<
      IApiResponse<IJobTemplate>,
      ICreateJobTemplatePayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.JobTemplates,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentJobTemplate", id: "LIST" }],
    }),

    /** Rename a template or move it to another shelf — only the company's own. */
    updateJobTemplate: builder.mutation<
      IApiResponse<IJobTemplate>,
      { id: number; body: { name?: string; category?: string } }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.Recruitment.JobTemplateById(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentJobTemplate", id: "LIST" }],
    }),

    deleteJobTemplate: builder.mutation<
      IApiResponse<{ message: string }>,
      number
    >({
      query: (id) => ({
        url: APIEndpoints.Recruitment.JobTemplateById(id),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "RecruitmentJobTemplate", id: "LIST" }],
    }),

    /** Put a candidate the company already knows (talent pool, past applicant) on a job. */
    addToPipeline: builder.mutation<
      IApiResponse<{ applicationId: number; created: boolean }>,
      { candidateId: number; jobId: number; matchScore?: number }
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.ApplicationsAddToPipeline,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
      ],
    }),

    parseBulkImport: builder.mutation<
      IApiResponse<IBulkImportParsedItem[]>,
      File[]
    >({
      query: (files) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: APIEndpoints.Recruitment.BulkImportParse,
          method: "POST",
          body: formData,
        };
      },
    }),

    confirmBulkImportToPool: builder.mutation<
      IApiResponse<{ created: number }>,
      { items: IBulkImportParsedItem[] }
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.BulkImportConfirmPool,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentTalentPool", id: "pool" }],
    }),

    confirmBulkImportToPipeline: builder.mutation<
      IApiResponse<{ created: number; skipped: number }>,
      { jobId: number; startStage: string; items: IBulkImportParsedItem[] }
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.BulkImportConfirmPipeline,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
      ],
    }),

    getRecruitmentDashboard: builder.query<
      IApiResponse<IRecruitmentDashboard>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.Dashboard }),
      providesTags: [{ type: "RecruitmentJob", id: "DASHBOARD" }],
    }),

    getRecruitmentDashboardOverview: builder.query<
      IApiResponse<IRecruitmentDashboardOverview>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.DashboardOverview }),
      providesTags: [{ type: "RecruitmentJob", id: "DASHBOARD" }],
    }),

    /** The funnel panel on its own, for its job select. */
    getRecruitmentDashboardFunnel: builder.query<
      IApiResponse<IDashboardFunnel>,
      { jobId: number | null }
    >({
      query: ({ jobId }) => ({
        url: APIEndpoints.Recruitment.DashboardFunnel,
        params: jobId === null ? undefined : { jobId },
      }),
    }),

    /** The pipeline chart on its own, for its tab strip and period select. */
    getRecruitmentDashboardPipeline: builder.query<
      IApiResponse<IDashboardPipeline>,
      { tab: DashboardPipelineTab; period: DashboardPipelinePeriod }
    >({
      query: (params) => ({
        url: APIEndpoints.Recruitment.DashboardPipeline,
        params,
      }),
    }),

    /**
     * The dashboard card's "View all" — every job's activity, merged, one page
     * at a time. `pageParam` is the next page number; `getNextPageParam` reads
     * `hasNextPage` off Jobniq's own pagination shape, passed straight through
     * by the proxy rather than translated.
     */
    getRecruitmentActivity: builder.infiniteQuery<
      IApiResponse<IJobActivityEntry[]>,
      void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) =>
          result.meta.hasNextPage ? (result.meta.page ?? 1) + 1 : undefined,
      },
      query: ({ pageParam }) => ({
        url: APIEndpoints.Recruitment.DashboardActivity,
        params: { page: pageParam, limit: 20 },
      }),
    }),

    getRecruitmentIntegrationStatus: builder.query<
      IApiResponse<IRecruitmentIntegrationStatus>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.IntegrationStatus }),
    }),

    getRecruitmentCountries: builder.query<
      IApiResponse<IRecruitmentCountry[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.IntegrationCountries }),
    }),

    /**
     * Cities, from Jobniq. `search` is passed through because the full list is
     * 1,028 rows — a picker that loads all of them before it can filter feels
     * broken on a slow connection.
     */
    getRecruitmentCities: builder.query<
      IApiResponse<IRecruitmentCity[]>,
      { countryId?: number; search?: string; limit?: number } | void
    >({
      query: (params) => ({
        url: APIEndpoints.Recruitment.IntegrationCities,
        params: params ?? undefined,
      }),
    }),

    getRecruitmentSettings: builder.query<
      IApiResponse<IRecruitmentSettings>,
      void
    >({
      query: () => ({ url: APIEndpoints.Recruitment.Settings }),
      providesTags: [{ type: "RecruitmentSettings", id: "SETTINGS" }],
    }),

    updateRecruitmentSettings: builder.mutation<
      IApiResponse<IRecruitmentSettings>,
      IUpdateRecruitmentSettingsPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.Settings,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentSettings", id: "SETTINGS" }],
    }),

    /*
      Settings → Careers page. Every write answers the whole theme, so the
      cache is replaced from the response rather than refetched.
    */
    getCareersTheme: builder.query<IApiResponse<ICareersTheme>, void>({
      query: () => ({ url: APIEndpoints.Recruitment.CareersTheme }),
      providesTags: [{ type: "RecruitmentSettings", id: "CAREERS_THEME" }],
    }),

    updateCareersTheme: builder.mutation<
      IApiResponse<ICareersTheme>,
      IUpdateCareersThemePayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.CareersTheme,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentSettings", id: "CAREERS_THEME" }],
    }),

    /** Uploads and switches the careers page to it. */
    setCareersThemeImage: builder.mutation<
      IApiResponse<ICareersTheme>,
      { kind: CareersImageKind; file: File }
    >({
      query: ({ kind, file }) => {
        const body = new FormData();
        body.append("file", file, file.name);
        return {
          url: careersImageUrl(kind),
          method: "PUT",
          body,
        };
      },
      invalidatesTags: [{ type: "RecruitmentSettings", id: "CAREERS_THEME" }],
    }),

    /** Drops the upload; the careers page goes back to the organization's. */
    removeCareersThemeImage: builder.mutation<
      IApiResponse<ICareersTheme>,
      CareersImageKind
    >({
      query: (kind) => ({
        url: careersImageUrl(kind),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "RecruitmentSettings", id: "CAREERS_THEME" }],
    }),

    getTalentPool: builder.query<
      IApiResponse<(ITalentPoolEntry | IPastApplicantRow | IReferral)[]>,
      IListTalentPoolParams
    >({
      query: (params) => ({ url: APIEndpoints.Recruitment.TalentPool, params }),
      providesTags: (_result, _error, { segment }) => [
        { type: "RecruitmentTalentPool", id: segment },
      ],
    }),

    addToTalentPool: builder.mutation<
      IApiResponse<ITalentPoolEntry>,
      IAddToTalentPoolPayload
    >({
      query: (body) => ({
        url: APIEndpoints.Recruitment.TalentPool,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RecruitmentTalentPool", id: "pool" }],
    }),

    removeFromTalentPool: builder.mutation<void, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.TalentPoolById(id),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "RecruitmentTalentPool", id: "pool" }],
    }),

    markReferralReviewed: builder.mutation<IApiResponse<IReferral>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.ReferralReviewed(id),
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "RecruitmentTalentPool", id: "referrals" }],
    }),

    rejectReferral: builder.mutation<IApiResponse<IReferral>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.ReferralReject(id),
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "RecruitmentTalentPool", id: "referrals" }],
    }),

    addReferralToPool: builder.mutation<IApiResponse<IReferral>, number>({
      query: (id) => ({
        url: APIEndpoints.Recruitment.ReferralAddToPool(id),
        method: "POST",
      }),
      invalidatesTags: [
        { type: "RecruitmentTalentPool", id: "referrals" },
        { type: "RecruitmentTalentPool", id: "pool" },
      ],
    }),

    addReferralToPipeline: builder.mutation<
      IApiResponse<IApplication>,
      { id: number; jobId: number }
    >({
      query: ({ id, jobId }) => ({
        url: APIEndpoints.Recruitment.ReferralAddToPipeline(id),
        method: "POST",
        body: { jobId },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "RecruitmentTalentPool", id: "referrals" },
        { type: "RecruitmentApplication", id: pipelineTag(jobId) },
      ],
    }),
  }),
});

export const {
  useGetJobsQuery,
  useGetJobFilterOptionsQuery,
  useGetRecruitmentJobCategoriesQuery,
  useGetJobExternalParticipantsQuery,
  useGetJobActivityQuery,
  useGetJobInsightsQuery,
  useGetJobSegmentCountsQuery,
  useGetJobByIdQuery,
  useLazyGetJobByIdQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  usePublishJobMutation,
  useCloseJobMutation,
  useArchiveJobMutation,
  useReopenJobMutation,
  useDuplicateJobMutation,
  useRestoreJobMutation,
  useDeleteJobMutation,
  useGetJobNotesQuery,
  useAddJobNoteMutation,
  useDeleteJobNoteMutation,
  useGetPipelineApplicationsQuery,
  useMoveApplicationStageMutation,
  useGetCandidatesQuery,
  useGetApplicationDetailQuery,
  useRecordScreeningDecisionMutation,
  useRecheckAiMatchMutation,
  useGetApplicationNotesQuery,
  useAddApplicationNoteMutation,
  useDeleteApplicationNoteMutation,
  useAiGenerateJobPostMutation,
  useAiGenerateJobDescriptionMutation,
  useAiSuggestJobRequirementsMutation,
  useAiGenerateScreeningQuestionsMutation,
  useAiSuggestInterviewPlanMutation,
  useAiRecommendInterviewTeamMutation,
  useAiGenerateEvaluationCriteriaMutation,
  useAiGenerateInterviewQuestionsMutation,
  useAiScoreJobMutation,
  useGetTalentPoolQuery,
  useAddToTalentPoolMutation,
  useRemoveFromTalentPoolMutation,
  useMarkReferralReviewedMutation,
  useRejectReferralMutation,
  useAddReferralToPoolMutation,
  useAddReferralToPipelineMutation,
  useRejectApplicationMutation,
  useAnalyzeCandidatesQuery,
  useCompareCandidatesQuery,
  useApproveFromAnalysisMutation,
  useCreateJobTemplateMutation,
  useUpdateJobTemplateMutation,
  useDeleteJobTemplateMutation,
  useGetJobTemplatesQuery,
  useAddToPipelineMutation,
  useParseBulkImportMutation,
  useConfirmBulkImportToPoolMutation,
  useConfirmBulkImportToPipelineMutation,
  useGetRecruitmentSettingsQuery,
  useUpdateRecruitmentSettingsMutation,
  useGetCareersThemeQuery,
  useUpdateCareersThemeMutation,
  useSetCareersThemeImageMutation,
  useRemoveCareersThemeImageMutation,
  useGetRecruitmentDashboardQuery,
  useGetRecruitmentDashboardOverviewQuery,
  useGetRecruitmentDashboardFunnelQuery,
  useGetRecruitmentDashboardPipelineQuery,
  useGetRecruitmentActivityInfiniteQuery,
  useGetRecruitmentIntegrationStatusQuery,
  useGetRecruitmentCountriesQuery,
  useGetRecruitmentCitiesQuery,
} = companyRecruitmentApi;
